import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { useCatalog } from '../../catalog/index.js'

import { Logger } from '../../../shared/utils/logger.js'
import { FETCH_STATUS } from '../../../shared/constants/fetchStatus.js'

import { useProductCapabilities } from '../hooks/useProductCapabilities.js'
import { useProductLayout } from '../hooks/useProductLayout.js'
import { useProductSupplements } from '../hooks/useProductSupplements.js'
import { useViewerModes } from '../hooks/useViewerModes.js'
import { clampInsolationAngle } from '../utils/svgInsolation.js'
import { COLOR_SCHEME, COLOR_SCHEME_ORDER } from '../constants/colorSchemes.js'
import { INSOLATION_DEFAULTS } from '../constants/overlayDefaults.js'

// ── ProductContext ────────────────────────────────────────────────────────────

const ProductContext = createContext(undefined)

/**
 * Clamps a variant index into the valid range [0, max - 1].
 * Non-finite or out-of-range inputs collapse to 0.
 */
function clampVariant(value, max) {
  const n = Number(value)
  const m = Math.max(1, Number(max) || 1)
  if (!Number.isFinite(n) || n < 0) return 0
  return Math.floor(n) % m
}

// ── NULL_PRODUCT_CONTEXT — default value returned before provider mounts ──────

const NULL_PRODUCT_CONTEXT = {
  selection: {
    product: null,
    productCategory: null,
    activeBuilding: null,
    activeTier: null,
  },

  capabilities: {
    hasViewer: false,
    hasCompartments: false,
    hasPricing: false,
    hasPriceHistory: false,
    hasSupplements: false,
    hasMonetaryObligations: false,
    hasRelatedProducts: false,
    hasResources: false,
    isMultiLevel: false,
    supportsLevelSelector: false,
    availableLevels: [],
    defaultLevel: null,
    hasAxonometricCamera: false,
    hasHotspotCameras: false,
    hasInsolationData: false,
    hasCompartmentVariants: false,
    allowsThreeDVariant: false,
    preferThreeD: false,
    // Project-level commissioning flags (app_config)
    hasInsolationCommissioned: false,
    hasArrangeCommissioned: false,
    hasAxonometricCommissioned: false,
    hasHotspotCommissioned: false,
    // Dataset-level checks (ANY product)
    datasetHasProductWithInsolation: false,
    datasetHasProductWithHotspots: false,
    datasetHasProductWithAxonometric: false,
    datasetHasProductWithCompartmentVariants: false,
    enabledModes: [],
    defaultMode: null,
    availableColorSchemes: [],
  },

  layout: {
    desktopPaneOrder: [],
    mobilePaneOrder: [],
    stickyPanes: new Set(),
    hiddenPanes: new Set(),
  },

  modes: {
    availableModes: [],
    activeMode: null,
    defaultMode: null,
    setActiveMode: () => {},
    resetActiveMode: () => {},
  },

  viewerState: {
    zoomLevel: 1,
    activeVariantId: null,
    activeHotspotId: null,
    selectedLevelId: null,
    selectedCompartmentId: null,
    hoveredCompartmentId: null,
    viewerVariant: null, // 2D or 3D
    isPanning: false,
    rotatedToNorth: false,
    activeColorScheme: COLOR_SCHEME.MONO,
    insolationAngleDeg: INSOLATION_DEFAULTS.initialAngleDeg,
    insolationFocusedCompartmentId: null,
    insolationHoveredHour: null,
    insolationHoveredPathBucket: null,
    infoLegendHoveredKey: null,
    infoHoveredPathKey: null,
    // Arrange mode: per-compartment variant indices (0-based), indexed by compartmentNumber - 1.
    selectedVariants: [],
    // Arrange mode: number of variants available per compartment (>= 1), aligned with selectedVariants.
    variantsCount: [],
  },

  viewerActions: {
    setZoomLevel: () => {},
    setActiveVariantId: () => {},
    setActiveHotspotId: () => {},
    setSelectedLevelId: () => {},
    setSelectedCompartmentId: () => {},
    setHoveredCompartmentId: () => {},
    setViewerVariant: () => {},
    setRotatedToNorth: () => {},
    setActiveColorScheme: () => {},
    setInsolationAngleDeg: () => {},
    setInsolationFocusedCompartmentId: () => {},
    setInsolationHoveredHour: () => {},
    setInsolationHoveredPathBucket: () => {},
    setInfoLegendHoveredKey: () => {},
    setInfoHoveredPathKey: () => {},
    setCompartmentVariant: () => {},
    cycleCompartmentVariant: () => {},
    setAllVariants: () => {},
    resetVariants: () => {},
    resetViewerState: () => {},
  },

  featureFlows: {
    openProductInquiry: () => {},
    openRelatedProducts: () => {},
    supplements: {
      forcedItems: [],
      selectedItems: [],
      availableItems: [],
      totalAdditive: 0,
      isPickerOpen: false,
      addItem: () => {},
      removeItem: () => {},
      openPicker: () => {},
      closePicker: () => {},
    },
    history: {
      isPriceHistoryOpen: false,
      openPriceHistory: () => {},
      closePriceHistory: () => {},
    },
    obligations: {
      isObligationsOpen: false,
      openObligations: () => {},
      closeObligations: () => {},
    }
  },

  urlState: {
    resolvedUrlState: null,
    applyUrlState: () => {},
    clearProductUrlState: () => {},
  },

  status: FETCH_STATUS.LOADING,
  error: null,

  // Stable refs bridging useAxonometricFrames ↔ ViewerToolbar compass.
  // compassElRef.current → CompassNorth DOM node (written by toolbar).
  // goToNorthRef.current → handleGoToNorth callback (written by renderer).
  axonometricBridge: { compassElRef: { current: null }, goToNorthRef: { current: null } },

  // Stable ref bridging TransformWrapper API (zoomIn, zoomOut, resetTransform) to ZoomControls.
  // PlanViewer writes it; ZoomControls reads it.
  zoomBridgeRef: { current: null },
}

// ── ProductContextProvider ────────────────────────────────────────────────────

/**
 * ProductContextProvider — aggregates product domain hooks and state.
 *
 * Mounts domain-specific hooks (`useProductCapabilities`, `useProductLayout`, `useViewerModes`, etc.)
 * and composes them into a single context value. Provider itself contains no business logic.
 *
 * Usage:
 * ```jsx
 * <ProductContextProvider product={...} capabilities={...}>
 *   <ProductView />
 * </ProductContextProvider>
 * ```
 *
 * @param {object} props
 * @param {object} props.children — components that will consume useProduct()
 * @param {object} [props.product] — active product from AppContext
 * @param {object} [props.productCategory] — business category identifier
 * @param {object[]} [props.allProducts] — full product list for global capability checks
 * @param {Record<string, unknown[]>} [props.interiorsData] — global interiors assets by camera name
 * @param {object} [props.activeBuilding] — active building from AppContext
 * @param {object} [props.activeTier] — active tier from AppContext
 * @returns {JSX.Element}
 */
export function ProductContextProvider({
  children,
  product = null,
  productCategory = null,
  allProducts = [],
  interiorsData = {},
  activeBuilding = null,
  activeTier = null,
  projectConfig = null,
  panelSettings = null,
}) {
  const { rawCmsData } = useCatalog()

  const capabilities = useProductCapabilities(
    projectConfig,
    panelSettings,
    product,
    productCategory,
    allProducts,
    interiorsData,
  )
  const layout = useProductLayout(panelSettings, capabilities)
  const modes = useViewerModes(capabilities, panelSettings?.unit_sheet_modes ?? null, productCategory)
  const supplements = useProductSupplements(product, panelSettings, rawCmsData)
  const [isPriceHistoryOpen, setIsPriceHistoryOpen] = useState(false)
  const [isObligationsOpen, setIsObligationsOpen] = useState(false)

  const [zoomLevel, setZoomLevel] = useState(1)
  const [activeVariantId, setActiveVariantId] = useState(null)
  const [activeHotspotId, setActiveHotspotId] = useState(null)
  const [selectedLevelId, setSelectedLevelId] = useState(capabilities.defaultLevel)
  const [selectedCompartmentId, setSelectedCompartmentId] = useState(null)
  const [hoveredCompartmentId, setHoveredCompartmentIdRaw] = useState(null)
  const [activeColorScheme, setActiveColorScheme] = useState(COLOR_SCHEME.MONO)
  const [insolationAngleDeg, setInsolationAngleDegRaw] = useState(INSOLATION_DEFAULTS.initialAngleDeg)
  const [insolationFocusedCompartmentId, setInsolationFocusedCompartmentId] = useState(null)
  const [insolationHoveredHour, setInsolationHoveredHour] = useState(null)
  const [insolationHoveredPathBucket, setInsolationHoveredPathBucket] = useState(null)
  const [infoLegendHoveredKey, setInfoLegendHoveredKey] = useState(null)
  const [infoHoveredPathKey, setInfoHoveredPathKey] = useState(null)
  const [rotatedToNorth, setRotatedToNorth] = useState(false)
  const [selectedVariants, setSelectedVariants] = useState([])
  const previousProductKeyRef = useRef(null)
  // In-memory restore of arrange-mode selections per product within the current session.
  // Key: product.uuid → number[] (selectedVariants snapshot).
  // Used by the product-change effect to restore on revisit; reset on page reload.
  const visitedVariantsRef = useRef(new Map())

  // Derived: number of variants per compartment, sorted by compartment.number,
  // value = max(versions, 1). Drives cycle bounds and disabled-tile UX.
  const variantsCount = useMemo(() => {
    const compartments = product?.compartments
    if (!Array.isArray(compartments) || compartments.length === 0) return []
    return [...compartments]
      .sort((a, b) => Number(a.number) - Number(b.number))
      .map((c) => Math.max(Number(c?.versions ?? c?.varsions ?? 1) || 1, 1))
  }, [product])

  // Stable bridge for the axonometric compass — created once, never replaced.
  // ViewerToolbar attaches its CompassNorth SVG to compassElRef.
  // PlanRendererAxonometricMode writes the current handleGoToNorth into goToNorthRef.
  const axonometricBridge = useMemo(
    () => ({ compassElRef: { current: null }, goToNorthRef: { current: null } }),
    [],
  )

  // Stable ref bridging TransformWrapper imperative API to ZoomControls.
  // Created once; PlanViewer passes it as ref to <TransformWrapper>.
  const zoomBridgeRef = useRef(null)

  const resolvedInitialLevel = activeTier ?? capabilities.defaultLevel ?? null

  useEffect(() => {
    const nextProductKey = product?.uuid ?? product?.id_cad ?? null
    const prevProductKey = previousProductKeyRef.current

    if (prevProductKey === nextProductKey) {
      return
    }

    // Snapshot outgoing arrange selections so a later revisit restores them.
    if (prevProductKey != null) {
      visitedVariantsRef.current.set(prevProductKey, selectedVariants)
    }

    previousProductKeyRef.current = nextProductKey
    // TODO: decide which state is worth preserving between product changes
    setZoomLevel(1)
    setRotatedToNorth(false)
    // setActiveVariantId(null)
    setActiveHotspotId(null)
    setSelectedLevelId(resolvedInitialLevel)
    // setSelectedCompartmentId(null)
    setHoveredCompartmentIdRaw(null)
    // setActiveColorScheme(COLOR_SCHEME.MONO)
    // setInsolationAngleDegRaw(INSOLATION_DEFAULTS.initialAngleDeg)
    setInsolationFocusedCompartmentId(null)

    // Restore arrange selections for previously visited products; otherwise reset to zeros.
    // Stored arrays may be stale relative to current variantsCount — clamp on read.
    const len = variantsCount.length
    const stored = nextProductKey != null ? visitedVariantsRef.current.get(nextProductKey) : null
    if (stored && stored.length === len) {
      setSelectedVariants(stored.map((v, i) => clampVariant(v, variantsCount[i])))
    } else {
      setSelectedVariants(new Array(len).fill(0))
    }
  }, [product?.uuid, product?.id_cad, resolvedInitialLevel, variantsCount, selectedVariants])

  const setInsolationAngleDeg = (next) => setInsolationAngleDegRaw(clampInsolationAngle(next))

  // ── Arrange-mode actions ─────────────────────────────────────────────────

  const setCompartmentVariant = useCallback((compartmentNumber, version) => {
    setSelectedVariants((prev) => {
      const idx = compartmentNumber - 1
      if (idx < 0 || idx >= prev.length) return prev
      const max = variantsCount[idx] ?? 1
      const next = clampVariant(version, max)
      if (prev[idx] === next) return prev
      const out = prev.slice()
      out[idx] = next
      return out
    })
  }, [variantsCount])

  const cycleCompartmentVariant = useCallback((compartmentNumber) => {
    setSelectedVariants((prev) => {
      const idx = compartmentNumber - 1
      if (idx < 0 || idx >= prev.length) return prev
      const max = variantsCount[idx] ?? 1
      if (max <= 1) return prev
      const out = prev.slice()
      out[idx] = (prev[idx] + 1) % max
      return out
    })
  }, [variantsCount])

  const resetVariants = useCallback(() => {
    setSelectedVariants((prev) => prev.every((v) => v === 0) ? prev : new Array(prev.length).fill(0))
  }, [])

  // setAllVariants — bulk replace selection. Clamps each value against the
  // current variantsCount; rejects size mismatches with a silent no-op.
  // Used by the on-entry preview animation to keep one render per tick.
  const setAllVariants = useCallback((next) => {
    if (!Array.isArray(next)) return
    setSelectedVariants((prev) => {
      if (next.length !== prev.length) return prev
      const out = next.map((v, i) => clampVariant(v, variantsCount[i] ?? 1))
      let changed = false
      for (let i = 0; i < out.length; i++) {
        if (out[i] !== prev[i]) { changed = true; break }
      }
      return changed ? out : prev
    })
  }, [variantsCount])

  // setHoveredCompartmentId — no-ops when the value is unchanged to avoid re-renders on every pointer move.
  const setHoveredCompartmentId = useCallback((next) => {
    const normalized = next == null ? null : Number(next)
    setHoveredCompartmentIdRaw((prev) => (prev === normalized ? prev : normalized))
  }, [])

  const openPriceHistory = useCallback(() => setIsPriceHistoryOpen(true), [])
  const closePriceHistory = useCallback(() => setIsPriceHistoryOpen(false), [])

  const openObligations = useCallback(() => setIsObligationsOpen(true), [])
  const closeObligations = useCallback(() => setIsObligationsOpen(false), [])

  const history = useMemo(() => ({
    isPriceHistoryOpen,
    openPriceHistory,
    closePriceHistory,
  }), [isPriceHistoryOpen, openPriceHistory, closePriceHistory])

  const obligations = useMemo(() => ({
    isObligationsOpen,
    openObligations,
    closeObligations,
  }), [isObligationsOpen, openObligations, closeObligations])

  // Derive available color schemes from panelSettings flags — ordered by COLOR_SCHEME_ORDER.
  // If unit_sheet_mode_info.color_schemes is absent, all schemes in COLOR_SCHEME_ORDER are available.
  const availableColorSchemes = useMemo(() => {
    const flags = panelSettings?.unit_sheet_mode_info?.color_schemes
    if (!flags) return COLOR_SCHEME_ORDER
    return COLOR_SCHEME_ORDER.filter((key) => flags[key] !== false)
  }, [panelSettings])

  const value = useMemo(
    () => ({
      ...NULL_PRODUCT_CONTEXT,
      selection: {
        product,
        productCategory,
        activeBuilding,
        activeTier,
      },
      capabilities: { ...capabilities, availableColorSchemes },
      layout,
      modes,
      viewerState: {
        ...NULL_PRODUCT_CONTEXT.viewerState,
        zoomLevel,
        activeVariantId,
        activeHotspotId,
        selectedLevelId,
        selectedCompartmentId,
        hoveredCompartmentId,
        viewerVariant: modes.activeViewerVariant,
        rotatedToNorth,
        activeColorScheme,
        insolationAngleDeg,
        insolationFocusedCompartmentId,
        insolationHoveredHour,
        insolationHoveredPathBucket,
        infoLegendHoveredKey,
        infoHoveredPathKey,
        selectedVariants,
        variantsCount,
      },
      viewerActions: {
        setZoomLevel,
        setActiveVariantId,
        setActiveHotspotId,
        setSelectedLevelId,
        setSelectedCompartmentId,
        setHoveredCompartmentId,
        setViewerVariant: modes.setViewerVariant,
        setRotatedToNorth,
        setActiveColorScheme,
        setInsolationAngleDeg,
        setInsolationFocusedCompartmentId,
        setInsolationHoveredHour,
        setInsolationHoveredPathBucket,
        setInfoLegendHoveredKey,
        setInfoHoveredPathKey,
        setCompartmentVariant,
        cycleCompartmentVariant,
        setAllVariants,
        resetVariants,
        resetViewerState: () => {
          setZoomLevel(1)
          setActiveVariantId(null)
          setActiveHotspotId(null)
          setSelectedLevelId(resolvedInitialLevel)
          setSelectedCompartmentId(null)
          setHoveredCompartmentId(null)
          setActiveColorScheme(COLOR_SCHEME.MONO)
          setRotatedToNorth(false)
          setInsolationAngleDegRaw(INSOLATION_DEFAULTS.initialAngleDeg)
          setInsolationFocusedCompartmentId(null)
          resetVariants()
        },
      },
      status: FETCH_STATUS.READY,
      featureFlows: {
        openProductInquiry: () => {},
        openRelatedProducts: () => {},
        supplements,
        history,
        obligations,
      },
      axonometricBridge,
      zoomBridgeRef,
    }),
    [
      product,
      axonometricBridge,
      productCategory,
      activeBuilding,
      activeTier,
      capabilities,
      availableColorSchemes,
      layout,
      modes,
      supplements,
      history,
      obligations,
      insolationAngleDeg,
      insolationFocusedCompartmentId,
      insolationHoveredHour,
      insolationHoveredPathBucket,
      infoLegendHoveredKey,
      infoHoveredPathKey,
      zoomLevel,
      activeVariantId,
      activeHotspotId,
      selectedLevelId,
      selectedCompartmentId,
      hoveredCompartmentId,
      activeColorScheme,
      rotatedToNorth,
      selectedVariants,
      variantsCount,
      setCompartmentVariant,
      cycleCompartmentVariant,
      setAllVariants,
      resetVariants,
      setHoveredCompartmentId,
      resolvedInitialLevel,
    ],
  )

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>
}

// ── useProduct hook ──────────────────────────────────────────────────────────

/**
 * useProduct — consumer hook for ProductContext.
 *
 * Reads aggregated product domain state. Must be called within ProductContextProvider.
 *
 * @throws {Error} if called outside ProductContextProvider
 * @returns {object} ProductContext value
 */
export function useProduct() {
  const ctx = useContext(ProductContext)

  if (!ctx) {
    const msg = 'useProduct must be used within ProductContextProvider'
    Logger.error({ message: msg, content: 'Missing ProductContextProvider wrapper' })
    throw new Error(msg)
  }

  return ctx
}
